import type { CodigoDeActivacionEmitido } from '@pasos/schemas';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Boton } from '../components/ui/Boton.js';
import { PACIENTE } from '../copy/index.js';
import * as api from './api.js';

/**
 * El código de activación, en Safari (INGRESO-2).
 *
 * Un solo bloque para los dos lugares donde se pide: el paso "Agregar a tu
 * pantalla de inicio" del onboarding (P-02d) y Perfil (P-13) del paciente y del
 * cuidador. En el iPhone, la app instalada no comparte la cookie con Safari:
 * este código es lo que la deja entrar la primera vez.
 *
 * **Se pide con un toque**, no al cargar: cada pedido anula el anterior, y un
 * código que nadie pidió es un código vivo de más.
 *
 * **Sin cuenta regresiva.** Para una persona mayor, un número que baja cada
 * segundo es ruido y apuro. Se dice cuánto vale, y cuando vence, el código se
 * reemplaza por el aviso y el botón para pedir otro: nada desaparece sin decir
 * por qué.
 */

type Estado =
  | { tipo: 'sin-pedir' }
  | { tipo: 'visible'; emitido: CodigoDeActivacionEmitido }
  | { tipo: 'vencido' };

/** "482913" se muestra "482 913": dos grupos de tres se copian sin perderse. */
function enDosGrupos(codigo: string): string {
  return `${codigo.slice(0, 3)} ${codigo.slice(3)}`;
}

export function MostrarCodigo({
  pedir = api.pedirCodigo,
}: {
  /** Inyectable: en los tests no hay servidor. */
  pedir?: () => Promise<CodigoDeActivacionEmitido>;
}) {
  const [estado, setEstado] = useState<Estado>({ tipo: 'sin-pedir' });
  const pedido = useMutation({
    mutationFn: pedir,
    onSuccess: (emitido) => setEstado({ tipo: 'visible', emitido }),
  });

  const venceAt = estado.tipo === 'visible' ? estado.emitido.venceAt : null;
  useEffect(() => {
    if (!venceAt) return;
    const falta = Math.max(0, Date.parse(venceAt) - Date.now());
    const reloj = setTimeout(() => setEstado({ tipo: 'vencido' }), falta);
    return () => clearTimeout(reloj);
  }, [venceAt]);

  const texto = PACIENTE.activacion;

  return (
    <section className="flex flex-col gap-4 rounded-lg bg-card p-5 text-left shadow-low outline-1 -outline-offset-1 outline-border">
      <p className="m-0 text-lg text-body">{texto.presentacion}</p>

      {/* Lo que cambia se anuncia: el código que aparece y el aviso de que venció. */}
      <div aria-live="polite" className="flex flex-col gap-4">
        {estado.tipo === 'visible' ? (
          <>
            <p className="m-0 flex flex-col gap-1">
              <span className="sr-only">{texto.codigoParaLector(estado.emitido.codigo)}</span>
              <span
                aria-hidden="true"
                className="text-display-xl font-bold tracking-widest text-heading tabular-nums"
              >
                {enDosGrupos(estado.emitido.codigo)}
              </span>
              <span className="text-md text-body">{texto.vale}</span>
            </p>
            <ol
              aria-label={texto.pasosParaLector}
              className="m-0 flex list-decimal flex-col gap-2 pl-6 text-lg text-heading"
            >
              {texto.pasos.map((paso) => (
                <li key={paso}>{paso}</li>
              ))}
            </ol>
          </>
        ) : null}

        {estado.tipo === 'vencido' ? (
          <p className="m-0 text-lg font-bold text-heading">{texto.vencido}</p>
        ) : null}

        {pedido.isError ? (
          <p className="m-0 text-md font-bold text-destructive-fg" role="alert">
            {texto.errorAlPedir}
          </p>
        ) : null}
      </div>

      {estado.tipo === 'visible' ? null : (
        <Boton cargando={pedido.isPending} onClick={() => pedido.mutate()} variante="teal">
          {estado.tipo === 'vencido' ? texto.otro : texto.mostrar}
        </Boton>
      )}
    </section>
  );
}
